<template>
  <div id="addProduct">
    <Loading v-if="loading"></Loading>
    <van-form @submit="onSubmit">
      <!-- 商品类型 -->
      <van-field
        autocomplete="off"
        :value="productTypeText"
        name="productType"
        label="商品类型"
        disabled
      />

      <!-- 基本信息 -->
      <van-cell-group inset>
        <div class="section-title">基本信息</div>
        <van-field
          autocomplete="off"
          v-model="formData.code"
          name="code"
          label="编号"
          disabled
        />

        <van-field
          autocomplete="off"
          v-model="formData.name"
          name="name"
          label="商品名称"
          required
        />

        <van-field
          autocomplete="off"
          v-model="formData.shortName"
          name="shortName"
          label="商品简称"
        />

        <van-cell
          title="图片"
          autocomplete="off"
          inputmode="none"
          v-model="formData.picture"
        >
          <UploadImage @change="imageChange"
        /></van-cell>

        <van-field
          autocomplete="off"
          label="外部编号"
          name="externalCode"
          v-model="formData.externalCode"
          allow-clear
          :class="{ 'external-code-highlight': externalCodeHighlight }"
        >
          <!-- <template #right-icon>
            <van-icon name="scan" @click="toScan"></van-icon>
          </template> -->
        </van-field>

        <van-cell @click="showDialog">
          <van-field
            autocomplete="off"
            inputmode="none"
            v-model="formData.categoryName"
            name="categoryName"
            label="商品类目"
            required
          >
          </van-field>
        </van-cell>

        <van-cell @click="showPlatformCategoryDialog">
          <van-field
            autocomplete="off"
            inputmode="none"
            v-model="formData.platformCategoryName"
            name="platformCategoryName"
            label="平台类目"
          >
          </van-field>
        </van-cell>

        <van-cell @click="showBrandDialog">
          <van-field
            autocomplete="off"
            inputmode="none"
            label="商品品牌"
            name="brandId"
            required
            v-model:value="formData.brandName"
          ></van-field>
        </van-cell>

        <van-field
          autocomplete="off"
          label="规格"
          name="spec"
          v-model:value="formData.spec"
          allow-clear
        >
        </van-field>

        <van-cell @click="showSupplierSelect">
          <van-field
            v-model="formData.supplierName"
            name="supplierName"
            inputmode="none"
            label="供应商"
            required
        /></van-cell>

        <van-field
          autocomplete="off"
          :value="businessModelText"
          name="businessModel"
          label="经营模式"
          readonly
        />

        <van-field
          autocomplete="off"
          v-model="formData.available"
          name="available"
          label="状态"
        >
          <template #input>
            <van-dropdown-menu style="width: 100%">
              <van-dropdown-item
                v-model="formData.available"
                :options="statusList"
              />
            </van-dropdown-menu>
          </template>
        </van-field>
      </van-cell-group>

      <!-- 属性价格 -->
      <van-cell-group inset>
        <div class="section-title">属性价格</div>
        <van-field autocomplete="off" name="isWeight" label="称重商品">
          <template #input>
            <van-radio-group v-model="formData.isWeight" direction="horizontal">
              <van-radio :name="1">是</van-radio>
              <van-radio :name="0">否</van-radio>
            </van-radio-group>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          label="单位"
          name="unit"
          v-model:value="formData.unit"
          allow-clear
          required
          @click="unitSelectVisible = true"
        />

        <UnitSelect :visible="unitSelectVisible" @confirm="unitSelectConfirm" />

        <van-field
          autocomplete="off"
          v-model="formData.weight"
          name="weight"
          type="number"
          label="固定重量"
        >
          <template #right-icon>
            <span>kg</span>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          v-model="formData.volume"
          name="volume"
          type="number"
          label="体积"
        >
          <template #right-icon>
            <span>cm³</span>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          v-model="formData.retailPrice"
          required
          type="number"
          name="retailPrice"
          label="零售价"
        >
          <template #right-icon>
            <span>{{ priceUnit }}</span>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          v-model="formData.purchasePrice"
          name="purchasePrice"
          type="number"
          label="采购价"
        >
          <template #right-icon>
            <span>{{ priceUnit }}</span>
          </template>
        </van-field>
      </van-cell-group>

      <!-- 其他价格 -->
      <van-cell-group inset>
        <div class="section-title">其他价格</div>
        <van-field
          autocomplete="off"
          v-model="formData.memberPrice"
          name="memberPrice"
          type="number"
          label="会员价"
        >
          <template #right-icon>
            <span>{{ priceUnit }}</span>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          v-model="formData.salePrice"
          name="salePrice"
          type="number"
          label="批发价"
        >
          <template #right-icon>
            <span>{{ priceUnit }}</span>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          v-model="formData.taxRate"
          name="taxRate"
          type="number"
          label="进项税率"
        >
          <template #right-icon>
            <span>%</span>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          v-model="formData.saleTaxRate"
          type="number"
          name="saleTaxRate"
          label="销项税率"
        >
          <template #right-icon>
            <span>%</span>
          </template>
        </van-field>

        <van-field
          autocomplete="off"
          v-model="formData.profitPercent"
          name="profitPercent"
          type="number"
          label="分销比例"
        >
          <template #right-icon>
            <span>%</span>
          </template>
        </van-field>
      </van-cell-group>

      <!-- 扩展信息 -->
      <van-cell-group inset>
        <div class="section-title">扩展信息</div>
        <van-field autocomplete="off" name="scoreFlag" label="积分赠送">
          <template #input>
            <van-radio-group
              v-model="formData.scoreFlag"
              direction="horizontal"
            >
              <van-radio :name="1">是</van-radio>
              <van-radio :name="0">否</van-radio>
            </van-radio-group>
          </template>
        </van-field>

        <van-field autocomplete="off" name="machFlag" label="无人柜商品">
          <template #input>
            <van-radio-group v-model="formData.machFlag" direction="horizontal">
              <van-radio :name="1">是</van-radio>
              <van-radio :name="0">否</van-radio>
            </van-radio-group>
          </template>
        </van-field>
      </van-cell-group>

      <div style="margin: 16px 0">
        <van-button
          round
          block
          type="primary"
          :loading="loading"
          native-type="submit"
        >
          提交
        </van-button>
      </div>
    </van-form>
    <CategorySelect
      :visible="categorySelectVisible"
      @confirm="categorySelectConfirm"
    ></CategorySelect>
    <CategorySelect
      :visible="platformCategorySelectVisible"
      :requestParams="{ platformFlag: 1 }"
      @confirm="platformCategorySelectConfirm"
    ></CategorySelect>

    <BrandSelect
      :visible="brandSelectVisible"
      @confirm="brandSelectConfirm"
    ></BrandSelect>
    <SupplierSelect
      :visible="supplierSelectVisible"
      @confirm="supplierSelectConfirm"
    ></SupplierSelect>
    <van-loading color="#fff" vertical class="loadingBox" v-if="loading"
      >正在采购...</van-loading
    >
  </div>
</template>
<script>
import { create } from "../../service/api/product";
import * as api from "../../service/api/index";

import { getCamelCharsUpperCase, cleanedStr } from "@/utils/index";
import { PRODUCTTYPE, AVAILABLE } from "../../utils/constants";
import CategorySelect from "../../components/categorySelect.vue";
import BrandSelect from "../../components/brandSelect.vue";
import { Toast, Dialog } from "vant";
import UploadImage from "../../components/uploadImage.vue";
import jsQR from "jsqr";
import Qrcode from "../../components/qrcodeScan.vue";
import SupplierSelect from "../../components/supplierSelect.vue";
import UnitSelect from "../../components/unitSelect.vue";
import Loading from "../../components/loading.vue";

export default {
  name: "index",
  components: {
    CategorySelect,
    BrandSelect,
    UploadImage,
    Qrcode,
    SupplierSelect,
    UnitSelect,
    Loading,
  },
  data() {
    return {
      formDataPic: null,
      loading: false,
      brandSelectVisible: false,
      supplierSelectVisible: false,
      categorySelectVisible: false,
      platformCategorySelectVisible: false,
      unitSelectVisible: false,
      productType: "1",
      scanBuffer: "",
      externalCodeHighlight: false,
      productTypeList: [
        { text: "普通商品", value: "1" },
        { text: "组合商品", value: "2" },
      ],
      id: this.$route.params.id,
      statusList: [
        { text: "启用", value: "1" },
        { text: "停用", value: "0" },
      ],
      formData: {
        productType: "1",
        taxRate: "0",
        saleTaxRate: "0",
        salePrice: "0",
        purchasePrice: "0",
        isWeight: 0,
        available: "1",
        name: "",
        retailPrice: "",
        categoryName: "",
        unit: "",
        supplierId: "",
        supplierName: "",
        businessModel: "",
        platformCategoryName: "",
        supCategoryId: "",
        spec: "",
        weight: "",
        volume: "",
        memberPrice: "",
        scoreFlag: 1,
        machFlag: 0,
        profitPercent: "",
        externalCode: "",
      },
      priceUnit: "元/件",
    };
  },
  computed: {
    businessModelText() {
      if (this.formData.businessModel == "1") return "经销";
      if (this.formData.businessModel == "2") return "联营";
      return this.formData.businessModel;
    },
    productTypeText() {
      if (this.formData.productType == "1") return "普通商品";
      if (this.formData.productType == "2") return "组合商品";
      return this.formData.productType;
    },
  },
  watch: {
    "formData.name": {
      handler(newVal, oldVal) {
        this.formData.shortName = cleanedStr(getCamelCharsUpperCase(newVal));
      },
    },
    "formData.unit": {
      handler(newVal) {
        this.priceUnit = newVal ? `元/${newVal}` : "元/件";
      },
      immediate: true,
    },
  },
  created() {
    try {
      this.formData.externalCode =
        localStorage.getItem("addScanText") || new Date().getTime();
    } catch (error) {
      this.formData.externalCode = new Date().getTime();
    }
  },
  mounted() {
    document.addEventListener("keydown", this.handleExternalCodeInput);
  },
  methods: {
    handleExternalCodeInput(event) {
      if (event.key.length === 1) {
        if (!/^\d$/.test(event.key)) {
          return;
        }
        this.scanBuffer += event.key;
        event.preventDefault();
      } else if (event.key === "Enter") {
        // 处理回车键
        if (this.scanBuffer) {
          if (this.scanBuffer.length >= 6 && this.scanBuffer.length <= 15) {
            this.formData.externalCode = this.scanBuffer;
            this.$set(this.formData, "externalCode", this.scanBuffer);
            // 添加高亮效果
            this.externalCodeHighlight = true;
            setTimeout(() => {
              this.externalCodeHighlight = false;
            }, 2000); // 2秒后恢复正常
          } else {
            Toast.fail("条形码有误，请重新扫描:" + this.scanBuffer);
          }
          this.scanBuffer = "";
        }
        event.preventDefault();
      }
    },
    toScan() {
      this.$router.push({
        name: "qrcodeScan",
        params: { type: "searchProduct" },
      });
    },
    // 调用移动端的扫码功能
    // 这里需要使用移动端的API，例如：
    // https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
    scanQRCode() {
      try {
        navigator.mediaDevices
          .getUserMedia({ video: true })
          .then(function (stream) {
            Toast.success(stream);
            const video = document.createElement("video");
            video.srcObject = stream;
            video.setAttribute("playsinline", true); // iOS使用
            video.play();
            Toast.success(video);
            requestAnimationFrame(function requestFrame() {
              const canvas = document.createElement("canvas");
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              const ctx = canvas.getContext("2d");
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              Toast.success(ctx);
              debugger;
              // const imageData = canvas.toDataURL("image/png");
              const imageData = ctx.getImageData(
                0,
                0,
                canvas.width,
                canvas.height
              );
              Toast.success(imageData);
              const code = jsQR(imageData.data, canvas.width, canvas.height);
              Toast.success(code);
              if (code) {
                Toast.success(code.data);
                stream.getTracks().forEach(function (track) {
                  track.stop();
                });
              }
              requestAnimationFrame(requestFrame);
            });
          })
          .catch(function (error) {
            Toast.fail(error);
            if (error["name"] === "NotAllowedError") {
              Toast.fail("Please allow camera access to scan QR codes.");
            } else if (error["name"] === "NotFoundError") {
              Toast.fail("No camera found on this device.");
            } else if (error["name"] === "NotSupportedError") {
              Toast.fail("The browser does not support camera access.");
            } else if (error["name"] === "NotReadableError") {
              Toast.fail("Could not access the camera. Is it already in use?");
            } else {
              Toast.fail("Unknown error occurred: " + error.message);
            }
          });
      } catch (error) {
        Toast.fail(error);
      }
    },
    imageChange(formData) {
      this.formDataPic = formData;
    },
    async images_upload_handler() {
      const res = await api.uploadImage(this.formDataPic);
      this.formData.picture = res.data;
    },
    validData() {
      if (this.$utils.isEmpty(this.formData.name)) {
        this.$utils.createError("商品名称不允许为空！");
        return false;
      }
      if (this.$utils.isEmpty(this.formData.categoryId)) {
        this.$utils.createError("商品类目不允许为空！");
        return false;
      }
      if (this.$utils.isEmpty(this.formData.supplierId)) {
        this.$utils.createError("供应商不允许为空！");
        return false;
      }
      if (this.$utils.isEmpty(this.formData.unit)) {
        this.$utils.createError("商品单位不允许为空！");
        return false;
      }

      if (!this.$utils.isEmpty(this.formData.weight)) {
        if (!this.$utils.isFloat(this.formData.weight)) {
          this.$utils.createError("重量（kg）必须为数字");
          return false;
        }
        if (!this.$utils.isFloatGeZero(this.formData.weight)) {
          this.$utils.createError("重量（kg）不允许小于0");
          return false;
        }
        if (!this.$utils.isNumberPrecision(this.formData.weight, 2)) {
          this.$utils.createError("重量（kg）最多允许2位小数");
          return false;
        }
      }
      if (!this.$utils.isEmpty(this.formData.saleTaxRate)) {
        if (!this.$utils.isFloat(this.formData.saleTaxRate)) {
          return Promise.reject("销项税率必须为数字");
        }
        if (!this.$utils.isFloatGeZero(this.formData.saleTaxRate)) {
          return Promise.reject("销项税率不允许小于0");
        }
        if (!this.$utils.isNumberPrecision(this.formData.saleTaxRate, 2)) {
          return Promise.reject("销项税率最多允许2位小数");
        }
      }
      if (!this.$utils.isEmpty(this.formData.purchaseTaxRate)) {
        if (!this.$utils.isFloat(this.formData.purchaseTaxRate)) {
          return Promise.reject("进项税率必须为数字");
        }
        if (!this.$utils.isFloatGeZero(this.formData.purchaseTaxRate)) {
          return Promise.reject("进项税率不允许小于0");
        }
        if (!this.$utils.isNumberPrecision(this.formData.purchaseTaxRate, 2)) {
          return Promise.reject("进项税率最多允许2位小数");
        }
      }
      if (!this.$utils.isEmpty(this.formData.volume)) {
        if (!this.$utils.isFloat(this.formData.volume)) {
          this.$utils.createError("体积（cm³）必须为数字");
          return false;
        }
        if (!this.$utils.isFloatGeZero(this.formData.volume)) {
          this.$utils.createError("体积（cm³）不允许小于0");
          return false;
        }
        if (!this.$utils.isNumberPrecision(this.formData.volume, 2)) {
          this.$utils.createError("体积（cm³）最多允许2位小数");
          return false;
        }
      }

      if (!this.$utils.isEmpty(this.formData.purchasePrice)) {
        if (!this.$utils.isFloat(this.formData.purchasePrice)) {
          this.$utils.createError("采购价（元）必须为数字");
          return false;
        }
        if (!this.$utils.isFloatGeZero(this.formData.purchasePrice)) {
          this.$utils.createError("采购价（元）不允许小于0");
          return false;
        }
        if (!this.$utils.isNumberPrecision(this.formData.purchasePrice, 2)) {
          this.$utils.createError("采购价（元）最多允许2位小数");
          return false;
        }
      }
      // if (this.$utils.isEmpty(this.formData.salePrice)) {
      //   this.$utils.createError("批发价（元）不允许为空！");
      //   return false;
      // }
      if (!this.$utils.isEmpty(this.formData.salePrice)) {
        if (!this.$utils.isFloat(this.formData.salePrice)) {
          this.$utils.createError("批发价（元）必须为数字");
          return false;
        }
        if (!this.$utils.isFloatGeZero(this.formData.salePrice)) {
          this.$utils.createError("批发价（元）不允许小于0");
          return false;
        }
        if (!this.$utils.isNumberPrecision(this.formData.salePrice, 2)) {
          this.$utils.createError("批发价（元）最多允许2位小数");
          return false;
        }
      }
      if (this.$utils.isEmpty(this.formData.retailPrice)) {
        this.$utils.createError("零售价（元）不允许为空！");
        return false;
      }
      if (!this.$utils.isEmpty(this.formData.retailPrice)) {
        if (!this.$utils.isFloat(this.formData.retailPrice)) {
          this.$utils.createError("零售价（元）必须为数字");
          return false;
        }
        if (!this.$utils.isFloatGeZero(this.formData.retailPrice)) {
          this.$utils.createError("零售价（元）不允许小于0");
          return false;
        }
        if (!this.$utils.isNumberPrecision(this.formData.retailPrice, 2)) {
          this.$utils.createError("零售价（元）最多允许2位小数");
          return false;
        }
      }
      // 会员价验证
      if (!this.$utils.isEmpty(this.formData.memberPrice)) {
        if (!this.$utils.isFloat(this.formData.memberPrice)) {
          this.$utils.createError("会员价（元）必须为数字");
          return false;
        }
        if (!this.$utils.isFloatGeZero(this.formData.memberPrice)) {
          this.$utils.createError("会员价（元）不允许小于0");
          return false;
        }
        if (!this.$utils.isNumberPrecision(this.formData.memberPrice, 2)) {
          this.$utils.createError("会员价（元）最多允许2位小数");
          return false;
        }
      }
      // 批发价验证
      if (!this.$utils.isEmpty(this.formData.salePrice)) {
        if (!this.$utils.isFloat(this.formData.salePrice)) {
          this.$utils.createError("批发价（元）必须为数字");
          return false;
        }
        if (!this.$utils.isFloatGeZero(this.formData.salePrice)) {
          this.$utils.createError("批发价（元）不允许小于0");
          return false;
        }
        if (!this.$utils.isNumberPrecision(this.formData.salePrice, 2)) {
          this.$utils.createError("批发价（元）最多允许2位小数");
          return false;
        }
      }
      return true;
    },
    categorySelectConfirm(row) {
      if (!Object.keys(row).length) {
        this.categorySelectVisible = false;
        return;
      }
      this.categorySelectVisible = false;
      this.formData.categoryName = row.name;
      this.formData.categoryId = row.id;
    },
    brandSelectConfirm(row) {
      if (!Object.keys(row).length) {
        this.brandSelectVisible = false;
        return;
      }
      this.brandSelectVisible = false;
      this.formData.brandName = row.name;
      this.formData.brandId = row.id;
    },
    showBrandDialog() {
      this.brandSelectVisible = true;
    },
    showSupplierSelect() {
      this.supplierSelectVisible = true;
    },
    supplierSelectConfirm(row) {
      if (Object.keys(row).length === 0) {
        this.supplierSelectVisible = false;
        return;
      }
      this.supplierSelectVisible = false;
      this.formData.supplierName = row.name;
      this.formData.supplierId = row.id;
      const code = row.code;
      if (code) {
        this.formData.businessModel = row.manageType;
      } else {
        this.formData.businessModel = undefined;
      }
    },
    unitSelectConfirm(item) {
      this.formData.unit = item.name;
      this.formData.unitCode = item.code;
      this.unitSelectVisible = false;
    },
    showDialog() {
      this.categorySelectVisible = true;
    },
    showPlatformCategoryDialog() {
      this.platformCategorySelectVisible = true;
    },
    categorySelectConfirm(row) {
      if (!Object.keys(row).length) {
        this.categorySelectVisible = false;
        return;
      }
      this.categorySelectVisible = false;
      this.formData.categoryName = row.name;
      this.formData.categoryId = row.id;
    },
    platformCategorySelectConfirm(row) {
      if (!Object.keys(row).length) {
        this.platformCategorySelectVisible = false;
        return;
      }
      this.platformCategorySelectVisible = false;
      this.formData.platformCategoryName = row.name;
      this.formData.supCategoryId = row.id;
    },
    async onSubmit() {
      if (!this.validData()) {
        return;
      }

      // 检查是否需要二次确认
      const retailPrice = parseFloat(this.formData.retailPrice);
      const purchasePrice = parseFloat(this.formData.purchasePrice);
      const memberPrice = parseFloat(this.formData.memberPrice);
      const salePrice = parseFloat(this.formData.salePrice);

      let needConfirm = false;
      let message = "";

      if (
        !isNaN(purchasePrice) &&
        !isNaN(retailPrice) &&
        purchasePrice > retailPrice
      ) {
        needConfirm = true;
        message += "采购价大于零售价，";
      }
      if (
        !isNaN(memberPrice) &&
        !isNaN(retailPrice) &&
        memberPrice > retailPrice
      ) {
        needConfirm = true;
        message += "会员价大于零售价，";
      }
      if (!isNaN(salePrice) && !isNaN(retailPrice) && salePrice > retailPrice) {
        needConfirm = true;
        message += "批发价大于零售价，";
      }

      if (needConfirm) {
        // 去除最后一个逗号
        message = message.slice(0, -1);
        message += "确定要保存吗？";

        try {
          await Dialog.confirm({
            title: "确认保存",
            message: message,
          });
        } catch (error) {
          // 用户取消保存
          return;
        }
      }

      try {
        if (this.formDataPic) {
          await this.images_upload_handler();
        }
        this.loading = true;
        delete this.formData.proChannel;

        const data = {
          ...this.formData,
          available: this.formData.available === "1" ? true : false,
        };
        const res = await create(data);
        if (res.code === 200) {
          Toast.success("新增商品成功");
          this.loading = false;
          this.$router.push({
            path: "/product/category",
            query: { type: "search" },
          });
        }
      } catch (error) {
        if (error.isCanceled) return;
        this.loading = false;
      }
    },
  },
  destroyed() {
    this.formData = {
      productType: "1",
      taxRate: "0",
      saleTaxRate: "0",
      salePrice: "0",
      purchasePrice: "0",
      isWeight: 0,
      available: "1",
      name: "",
      retailPrice: "",
      categoryName: "",
      unit: "",
      supplierId: "",
      supplierName: "",
      businessModel: "",
      platformCategoryName: "",
      supCategoryId: "",
      spec: "",
      weight: "",
      volume: "",
      memberPrice: "",
      scoreFlag: 1,
      machFlag: 0,
      profitPercent: "",
    };
    this.scanBuffer = "";
    document.removeEventListener("keydown", this.handleExternalCodeInput);
  },
};
</script>

<style scoped lang="less">
::v-deep .external-code-highlight {
  animation: highlight 2s ease-in-out;
  background-color: rgba(0, 191, 165, 0.1);
}
@keyframes highlight {
  0% {
    background-color: rgba(0, 191, 165, 0.1);
  }
  50% {
    background-color: rgba(0, 191, 165, 0.3);
  }
  100% {
    background-color: rgba(0, 191, 165, 0.1);
  }
}

::v-deep .van-field__body {
  height: 100%;
}
.van-form {
  padding: 10px;
}
.section-title {
  font-size: 14px;
  font-weight: bold;
  color: #333;
  margin: 15px 0 8px 0;
  padding-left: 5px;
}

.van-cell-group--inset {
  margin: 0;
}
::v-deep .van-cell-group {
  margin-top: 15px;
}
.loadingBox {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  justify-content: center;
}
.van-icon {
  font-size: 22px;
}
</style>
